import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { DockAreaComponent } from '@shared/floating-panel/dock-area.component';
import { DockFixedTabDirective } from '@shared/floating-panel/dock-fixed-tab.directive';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { USER_QUERIES } from '@/app/platform/platform-core/user/store/types';
import { postLoginRedirectStorageKey } from '@/app/platform/platform-core/user/constants';
import { AccountPublicApiService, PublicRegistrationConfig } from '@/app/platform/platform-core/user/services/account-public-api.service';
import { EgiPageNavigationService } from '@/app/platform/platform-context/egi-page-navigation.service';
import { ViewportService } from '@core/services/viewport.service';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { HD_CHART_ENVIRONMENT_PAGE_ID } from '@/app/egi.pages/human-design/chart.pages/hd-page-ids';
import { HdChartShareService } from '@/app/human-design/chart/library/hd-chart-share.service';
import { HdSharedChartLoadError, HdSharedChartResult } from '@/app/human-design/chart/library/hd-chart-share.models';
import { HdSharedChartBannerComponent } from '@/app/human-design/chart/library/hd-shared-chart-banner.component';
import { createDefaultHdChartGlobalSettings } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-global-settings';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { HdPlanetInfoService } from '@/app/human-design/chart/bodygraph/hd-planet-info.service';
import { HdBodygraphRenderStateFactory } from '@/app/human-design/chart/bodygraph/hd-bodygraph-render-state.factory';
import { HdBodygraphTransitOverlay } from '@/app/human-design/chart/bodygraph/hd-bodygraph-transit-overlay.models';
import { HdBodygraphLocksKeysOverlay } from '@/app/human-design/chart/bodygraph/hd-bodygraph-locks-keys-layer';
import { HdChartHoverHighlightService } from '@/app/human-design/chart/bodygraph/hd-chart-hover-highlight.service';
import { applyHdHoverHighlightTheme } from '@/app/human-design/chart/bodygraph/hd-hover-highlight-theme';
import { HdChironPosition, resolveHdChironPosition, toHdSimpleChironVm } from '@/app/human-design/chart/bodygraph/hd-chiron.models';
import { filterHdCycleOverlayActivations } from '@/app/human-design/chart/bodygraph/hd-cycle-overlay.utils';
import { HdBodygraphChartComponent } from '@/app/human-design/chart/bodygraph/hd-bodygraph-chart/hd-bodygraph-chart.component';
import { HdBasicPropertiesComponent } from '@/app/human-design/chart/bodygraph/basic-properties/hd-basic-properties.component';
import { HdAdvancedPropertiesComponent } from '@/app/human-design/chart/bodygraph/advanced-properties/hd-advanced-properties.component';
import { HdChartShortcutBarComponent } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-shortcut-bar.component';
import { HdPlanetGridComponent } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-grid/hd-planet-grid.component';
import {
  HdJustNowBasePlanetGridComponent,
  HdJustNowBasePlanetGridLabels,
} from '@/app/human-design/chart/bodygraph/planet-grid/hd-just-now-base-planet-grid/hd-just-now-base-planet-grid.component';
import { HdJustNowLineDataResolver } from '@/app/human-design/chart/bodygraph/planet-grid/hd-just-now-fixing.utils';
import { HdSimpleExtraPlanetVm } from '@/app/human-design/chart/bodygraph/planet-grid/hd-simple-planet-grid/hd-simple-planet-grid.component';
import { getHdPlanetDisplayConfigByPlanetId } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-display-config';
import { HD_JUST_NOW_TRANSIT_TEXT_COLOR } from '@/app/human-design/chart/mandala/just-now-transit/hd-just-now-transit';
import { HdLocksKeysStateService } from '@/app/human-design/chart/mandala/locks-keys/hd-locks-keys-state.service';
import { LanguageSelectorComponent } from '../public-account/language-selector/language-selector.component';
import { applyHdGlobalShiftMarkers } from '@/app/human-design/chart/hd-global-shift';

type SharedChartPageState = 'loading' | 'ready' | 'invalid' | 'busy' | 'redirecting';
type SharedChartPanel = 'basic' | 'advanced';

/**
 * Publieke gastpagina van een deellink (spec 2026-10-20-chart-share §4.3/§4.4):
 * `/#/shared/:token`, buiten de guards. Ingelogd → meteen door naar de chartomgeving
 * (gedeelde tab). Niet ingelogd → de chart met de standaardsettings (defaults, niet de
 * localStorage van de browser): volledige bodygraph met poortnummers, cycle-/composite-
 * overlay, de planetengrids en de hover-koppeling zoals in de omgeving, Basis- en
 * Geavanceerd-paneel in gaststand in dezelfde dock-tabs als de omgeving, en een gedimde
 * shortcutbalk. Elke klik die iets zou openen meldt dat een account nodig is, met een
 * knop naar de login die daarna terugkeert naar deze link.
 */
@Component({
  selector: 'shared-chart-page',
  standalone: true,
  imports: [
    SharedModule,
    TranslocoModule,
    DockAreaComponent,
    DockFixedTabDirective,
    LanguageSelectorComponent,
    HdSharedChartBannerComponent,
    HdBodygraphChartComponent,
    HdBasicPropertiesComponent,
    HdAdvancedPropertiesComponent,
    HdChartShortcutBarComponent,
    HdPlanetGridComponent,
    HdJustNowBasePlanetGridComponent,
  ],
  // Hover-koppeling chart ↔ planetengrid ↔ panelen: page-scoped, zoals de omgeving.
  providers: [HdChartHoverHighlightService],
  templateUrl: './shared-chart-page.component.html',
  styleUrl: './shared-chart-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedChartPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly userQueries = inject(USER_QUERIES);
  private readonly pageNavigation = inject(EgiPageNavigationService);
  private readonly shareService = inject(HdChartShareService);
  private readonly accountApi = inject(AccountPublicApiService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);
  private readonly hoverHighlight = inject(HdChartHoverHighlightService);
  private readonly locksKeysState = inject(HdLocksKeysStateService);
  private readonly floatingPanels = inject(FloatingPanelService);
  protected readonly viewport = inject(ViewportService);

  /** Standaardsettings (spec §4.4): bewust de defaults, niet de settings-store van deze browser. */
  protected readonly defaults = createDefaultHdChartGlobalSettings();
  protected readonly cycleTitleColor = HD_JUST_NOW_TRANSIT_TEXT_COLOR;

  /** Op template-ref (niet op type) zodat een spec de dock-area kan stubben. */
  private readonly dockArea = viewChild<DockAreaComponent>('dock');

  protected readonly token = this.route.snapshot.paramMap.get('token') ?? '';
  protected readonly state = signal<SharedChartPageState>('loading');
  protected readonly shared = signal<HdSharedChartResult | null>(null);
  protected readonly registrationEnabled = signal(false);

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      accountRequired: translate('humanDesign.chartShare.guest.accountRequired'),
      later: translate('humanDesign.chartShare.guest.later'),
      login: translate('humanDesign.chartShare.banner.login'),
      invalidLink: translate('humanDesign.chartShare.guest.invalidLink'),
      tooManyRequests: translate('humanDesign.chartShare.guest.tooManyRequests'),
      retry: translate('humanDesign.chartShare.guest.retry'),
      loading: translate('humanDesign.chartShare.guest.loading'),
      tabBasic: translate('humanDesign.chartShare.guest.tabBasic'),
      tabAdvanced: translate('humanDesign.chartShare.guest.tabAdvanced'),
      toLogin: translate('humanDesign.chartShare.guest.toLogin'),
    };
  });

  /** Met de shift-markering (hd-global-shift.ts), zoals in de omgeving. */
  protected readonly preset = computed(() => {
    const preset = this.shared()?.preset;
    return preset == null ? null : applyHdGlobalShiftMarkers(preset);
  });
  protected readonly chartType = computed(() => this.shared()?.meta.type ?? PlatformChartType.Standard);
  protected readonly planetaryActivations = computed(() => this.preset()?.planetaryActivations ?? []);
  protected readonly designActivations = computed(() => this.planetaryActivations().filter(a => a.side === 'design'));
  protected readonly personalityActivations = computed(() => this.planetaryActivations().filter(a => a.side === 'personality'));
  protected readonly hasPlanetaryActivations = computed(() => this.planetaryActivations().length > 0);
  protected readonly renderState = computed(() => {
    const configuration = this.preset()?.configuration;
    return configuration ? this.renderStateFactory.create(configuration) : null;
  });

  // ── Cycle/composite (spec §4.4: zoals de omgeving, zonder haar extra's) ──
  protected readonly cycleInfo = computed(() => this.preset()?.cycle ?? null);
  protected readonly compositeInfo = computed(() => this.preset()?.composite ?? null);
  protected readonly isCycleOrComposite = computed(() => this.cycleInfo() != null || this.compositeInfo() != null);
  /** Cycle-/composite-charts openen op Geavanceerd, zoals de omgeving dat doet. */
  protected readonly defaultPanel = computed<SharedChartPanel>(() => (this.isCycleOrComposite() ? 'advanced' : 'basic'));
  /** De "tweede kant": het cycle-moment of de composite-partner (groene overlay). */
  private readonly overlayMomentChart = computed(() =>
    this.cycleInfo()?.moment ?? this.compositeInfo()?.partnerChart ?? null);
  protected readonly cycleOverlay = computed<HdBodygraphTransitOverlay | null>(() => {
    const moment = this.overlayMomentChart();
    if (moment == null) return null;
    const activations = filterHdCycleOverlayActivations(moment.planetaryActivations);
    return activations.length === 0 ? null : { activations, colorMode: 'two-color' };
  });
  protected readonly cycleMomentGridActivations = computed(() => this.cycleOverlay()?.activations ?? []);
  /** Locks & keys op de bodygraph (drie-standen-toggle in Geavanceerd), zoals de omgeving. */
  protected readonly locksKeysChartOverlay = computed<HdBodygraphLocksKeysOverlay | null>(() => {
    const mode = this.locksKeysState.chartOverlayMode();
    return mode === 'off' ? null : { periodId: this.locksKeysState.periodId(), mode };
  });
  protected readonly compositeChart1Label = computed(() => this.compositeInfo()?.source.name ?? null);
  protected readonly compositeChart2Label = computed(() => this.compositeInfo()?.partner.name ?? null);

  // ── Chiron (informatief, zoals de omgeving met showChiron aan) ────────────
  protected readonly designChiron = computed(() => this.simpleChiron(resolveHdChironPosition(this.preset()?.metadata, 'design')));
  protected readonly personalityChiron = computed(() => this.simpleChiron(resolveHdChironPosition(this.preset()?.metadata, 'personality')));
  protected readonly cycleMomentDesignChiron = computed(() =>
    this.simpleChiron(resolveHdChironPosition(this.overlayMomentChart()?.metadata, 'design')));
  protected readonly cycleMomentPersonalityChiron = computed(() =>
    this.simpleChiron(resolveHdChironPosition(this.overlayMomentChart()?.metadata, 'personality')));

  // ── Cycle-grids: dezelfde resolvers als de omgeving ───────────────────────
  protected readonly justNowBasePlanetGridLabels = computed<HdJustNowBasePlanetGridLabels>(() => {
    const lang = this.contentLang.effectiveLang();
    const prefix = 'humanDesign.chart.justNowTransit.planetGrid';
    return {
      title: this.transloco.translate(`${prefix}.baseTitle`, undefined, lang),
      exaltation: this.transloco.translate(`${prefix}.exaltation`, undefined, lang),
      detriment: this.transloco.translate(`${prefix}.detriment`, undefined, lang),
      sameGate: this.transloco.translate(`${prefix}.sameGate`, undefined, lang),
      harmonicGate: this.transloco.translate(`${prefix}.harmonicGate`, undefined, lang),
    };
  });

  protected readonly justNowTransitLineData: HdJustNowLineDataResolver = (gate, line) => {
    const translation = this.gateInfo.gateInfoIn(gate, this.contentLang.effectiveLang()).translations.lines[line - 1];
    if (translation == null) return null;
    return { exaltationPlanet: translation.exaltation.planet, detrimentPlanet: translation.detriment.planet };
  };

  protected readonly justNowTransitPlanetName = (planetId: number): string => {
    const config = getHdPlanetDisplayConfigByPlanetId(planetId);
    if (config == null) return `${planetId}`;
    return this.planetInfo.planetInfoIn(config.key, this.contentLang.effectiveLang()).name;
  };

  constructor() {
    if (this.userQueries.getIsAnyUserAuthenticated()) {
      // Ingelogd: de chartomgeving opent de gedeelde tab (of de eigen chart).
      const result = this.pageNavigation.navigateToPage(HD_CHART_ENVIRONMENT_PAGE_ID, { sharedToken: this.token });
      if (result !== false) {
        this.state.set('redirecting');
        return;
      }
    }

    // Hover-koppeling met de standaardsettings (zelfde wiring als de chart-pagina);
    // poortstukken-highlight staat op de gastpagina bewust uit (Steven 2026-10-20).
    this.hoverHighlight.setKeynotesAtHoverOnly(this.defaults.keynotesAtHoverOnly);
    this.hoverHighlight.setDimOnHover(this.defaults.dimOnHover);
    this.hoverHighlight.setDimOneSideOnly(this.defaults.dimOneSideOnly);
    this.hoverHighlight.setHighlightGatePieces(false);
    effect(() => this.hoverHighlight.setEnabled(this.preset() != null));
    // Hoverkleur (default groen) als CSS-variabelen op <html>, zoals de HD-app-shell dat doet;
    // bij het verlaten weer weg zodat de rest van de app op zijn eigen kleur terugvalt.
    const rootStyle = inject(DOCUMENT).documentElement.style;
    applyHdHoverHighlightTheme(rootStyle, this.defaults.hoverHighlightColor);
    inject(DestroyRef).onDestroy(() => applyHdHoverHighlightTheme(rootStyle, null));

    // Locks & Keys-basisinfo in Geavanceerd leest de gedeelde L&K-staat (zoals de omgeving die voedt).
    effect(() => {
      const preset = this.preset();
      const moment = this.overlayMomentChart();
      const composite = this.compositeInfo();
      untracked(() => {
        this.locksKeysState.chartActivations.set(preset?.configuration.activations ?? []);
        this.locksKeysState.chartPlanetaryActivations.set(preset?.planetaryActivations ?? []);
        this.locksKeysState.partnerActivations.set(moment?.configuration.activations ?? []);
        this.locksKeysState.partnerPlanetaryActivations.set(moment?.planetaryActivations ?? []);
        this.locksKeysState.chart1Name.set(composite?.source.name ?? '');
        this.locksKeysState.chart2Name.set(composite?.partner.name ?? '');
      });
    });
    inject(DestroyRef).onDestroy(() => {
      this.locksKeysState.chartActivations.set([]);
      this.locksKeysState.chartPlanetaryActivations.set([]);
      this.locksKeysState.partnerActivations.set([]);
      this.locksKeysState.partnerPlanetaryActivations.set([]);
    });

    // Starttab: Geavanceerd voor cycle/composite; de dock-area bestaat pas na het laden.
    // `untracked`: activateFixedTab leest de tabstate; anders zou dit effect bij elke
    // tabwissel van de kijker opnieuw lopen en de starttab terugzetten.
    effect(() => {
      const dock = this.dockArea();
      const panel = this.defaultPanel();
      if (dock && this.shared()) untracked(() => dock.activateFixedTab(panel));
    });

    // Elke info-/paneelklik, hoe diep ook (I Tjing-kaart, Locks & Keys, fixed stars,
    // love gates, kruisen in de grid): geen paneel, wel de accountmelding.
    const unregisterOpenGuard = this.floatingPanels.setOpenGuard(() => {
      void this.accountRequired();
      return false;
    });
    inject(DestroyRef).onDestroy(unregisterOpenGuard);

    void this.load();
    void this.loadRegistrationConfig();
  }

  private async load(): Promise<void> {
    try {
      this.shared.set(await this.shareService.getShared(this.token));
      this.state.set('ready');
    } catch (e) {
      // 429 = anonieme limiet van de link bereikt: even wachten, niet "ongeldig".
      const busy = e instanceof HdSharedChartLoadError && e.kind === 'tooManyRequests';
      this.state.set(busy ? 'busy' : 'invalid');
    }
  }

  /** Na een 429: opnieuw proberen (de limiet is per minuut). */
  protected retry(): void {
    this.state.set('loading');
    void this.load();
  }

  private async loadRegistrationConfig(): Promise<void> {
    try {
      const result = await this.accountApi.getRegistrationConfig();
      const config = result.value as PublicRegistrationConfig | undefined;
      this.registrationEnabled.set(result.isValid && config?.registrationEnabled === true);
    } catch {
      this.registrationEnabled.set(false);
    }
  }

  /** Elke klik op iets dat een paneel of functie zou openen. */
  protected async accountRequired(): Promise<void> {
    const t = this.text();
    const login = await this.confirmer.confirm(t.accountRequired, {
      color: 'primary',
      confirmationText: t.login,
      refusalText: t.later,
      hideTitle: true,
    });
    if (login) this.goToLogin();
  }

  protected goToLogin(): void {
    this.rememberReturn();
    void this.router.navigate(['/login']);
  }

  protected goToRegister(): void {
    this.rememberReturn();
    void this.router.navigate(['/register']);
  }

  private simpleChiron(position: HdChironPosition | null): HdSimpleExtraPlanetVm | null {
    if (position == null) return null;
    return toHdSimpleChironVm(
      position,
      this.transloco.translate('humanDesign.chart.simpleGrid.chironTooltip', undefined, this.contentLang.effectiveLang()),
    );
  }

  /** Na inloggen/registreren komt de kijker op deze deellink terug (StoreUserActivator). */
  private rememberReturn(): void {
    try {
      sessionStorage.setItem(postLoginRedirectStorageKey, `/shared/${this.token}`);
    } catch {
      // Privémodus zonder storage: de kijker plakt de link dan zelf opnieuw.
    }
  }
}
